import React, { useEffect, useState } from 'react';
import { Button, Form, Label, Modal, Pagination, Table } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { API, showError, showSuccess } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';
import { renderQuota } from '../helpers/render';
import { useTranslation } from 'react-i18next';

const KEY_TYPE_OPTIONS = [
  { key: '1', value: '1', text: '1 企业名称' },
  { key: '2', value: '2', text: '2 信用代码' },
  { key: '3', value: '3', text: '3 注册号' },
  { key: '4', value: '4', text: '4 注册地址' },
  { key: '5', value: '5', text: '5 经营范围' },
  { key: '6', value: '6', text: '6 法人' },
  { key: '7', value: '7', text: '7 股东' },
];

const ApiProductsTable = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [testOpen, setTestOpen] = useState(false);
  const [testProduct, setTestProduct] = useState(null);
  const [testKey, setTestKey] = useState('小米');
  const [testKeyType, setTestKeyType] = useState('1');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadProducts = async (startIdx) => {
    const res = await API.get(`/api/api_product/?p=${startIdx}`);
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setProducts(data);
      } else {
        setProducts((prev) => [...prev, ...data]);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadProducts(0).catch((reason) => showError(reason));
  }, []);

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(products.length / ITEMS_PER_PAGE) + 1) {
        await loadProducts(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  const searchProducts = async () => {
    if (searchKeyword === '') {
      await loadProducts(0);
      setActivePage(1);
      return;
    }
    const res = await API.get(
      `/api/api_product/search?keyword=${encodeURIComponent(searchKeyword)}`
    );
    const { success, message, data } = res.data;
    if (success) {
      setProducts(data);
      setActivePage(1);
    } else {
      showError(message);
    }
  };

  const manageProduct = async (id, action, idx) => {
    let res;
    if (action === 'delete') {
      res = await API.delete(`/api/api_product/${id}`);
    } else {
      const product = products[(activePage - 1) * ITEMS_PER_PAGE + idx];
      res = await API.put('/api/api_product/', {
        ...product,
        status: action === 'enable' ? 1 : 2,
      });
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess('操作成功');
      const realIdx = (activePage - 1) * ITEMS_PER_PAGE + idx;
      const next = [...products];
      if (action === 'delete') {
        next.splice(realIdx, 1);
      } else {
        next[realIdx] = { ...next[realIdx], status: action === 'enable' ? 1 : 2 };
      }
      setProducts(next);
    } else {
      showError(message);
    }
  };

  const openTest = (product) => {
    setTestProduct(product);
    setTestKey('小米');
    setTestKeyType('1');
    setTestResult(null);
    setTestOpen(true);
  };

  const runTest = async () => {
    if (!testProduct) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await API.post(`/api/api_product/test/${testProduct.id}`, {
        key: testKey,
        key_type: testKeyType,
        page_index: 1,
        page_size: 5,
      });
      const { success, message, data } = res.data || {};
      if (success) {
        setTestResult(data);
        showSuccess('测试成功');
      } else {
        setTestResult(data || null);
        showError(message || '测试失败');
      }
    } catch (e) {
      showError(e.message || '测试失败');
    }
    setTesting(false);
  };

  const sampleRows = () => {
    const list =
      testResult &&
      testResult.response &&
      testResult.response.data &&
      testResult.response.data.data;
    return Array.isArray(list) ? list.slice(0, 5) : [];
  };

  return (
    <>
      <Form onSubmit={searchProducts}>
        <Form.Input
          icon='search'
          fluid
          iconPosition='left'
          placeholder='搜索名称或路径...'
          value={searchKeyword}
          onChange={(e, { value }) => setSearchKeyword(value.trim())}
        />
      </Form>
      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>ID</Table.HeaderCell>
            <Table.HeaderCell>名称</Table.HeaderCell>
            <Table.HeaderCell>方法</Table.HeaderCell>
            <Table.HeaderCell>路径</Table.HeaderCell>
            <Table.HeaderCell>价格</Table.HeaderCell>
            <Table.HeaderCell>缓存(秒)</Table.HeaderCell>
            <Table.HeaderCell>状态</Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {products
            .slice((activePage - 1) * ITEMS_PER_PAGE, activePage * ITEMS_PER_PAGE)
            .map((product, idx) => (
              <Table.Row key={product.id}>
                <Table.Cell>{product.id}</Table.Cell>
                <Table.Cell>{product.name}</Table.Cell>
                <Table.Cell>{product.method}</Table.Cell>
                <Table.Cell>/openapi{product.path}</Table.Cell>
                <Table.Cell>
                  {product.price > 0 ? (
                    renderQuota(product.price, t)
                  ) : (
                    <Label basic size='small'>
                      免费
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell>{product.cache_ttl}</Table.Cell>
                <Table.Cell>
                  {product.status === 1 ? (
                    <Label basic color='green'>
                      启用
                    </Label>
                  ) : (
                    <Label basic color='red'>
                      禁用
                    </Label>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <div>
                    <Button
                      size={'tiny'}
                      as={Link}
                      to={'/api-product/edit/' + product.id}
                    >
                      编辑
                    </Button>
                    <Button
                      size={'tiny'}
                      onClick={() => {
                        manageProduct(
                          product.id,
                          product.status === 1 ? 'disable' : 'enable',
                          idx
                        );
                      }}
                    >
                      {product.status === 1 ? '禁用' : '启用'}
                    </Button>
                    <Button
                      size={'tiny'}
                      primary
                      onClick={() => openTest(product)}
                    >
                      测试
                    </Button>
                    <Button
                      size={'tiny'}
                      negative
                      onClick={() => {
                        manageProduct(product.id, 'delete', idx);
                      }}
                    >
                      删除
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='8'>
              <Button size='small' as={Link} to='/api-product/add' loading={loading}>
                新建 API
              </Button>
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={onPaginationChange}
                size='small'
                siblingRange={1}
                totalPages={
                  Math.ceil(products.length / ITEMS_PER_PAGE) +
                  (products.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
                }
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      <Modal open={testOpen} onClose={() => setTestOpen(false)} size='large'>
        <Modal.Header>
          测试 {testProduct ? testProduct.name : ''}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths='equal'>
              <Form.Input
                label='关键词'
                value={testKey}
                onChange={(e, { value }) => setTestKey(value)}
              />
              <Form.Select
                label='key_type'
                options={KEY_TYPE_OPTIONS}
                value={testKeyType}
                onChange={(e, { value }) => setTestKeyType(value)}
              />
            </Form.Group>
            <Button type='button' primary loading={testing} onClick={runTest}>
              开始测试
            </Button>
          </Form>
          {testResult && (
            <div style={{ marginTop: 16 }}>
              <p>
                数据来源：
                <Label color={testResult.source === 'local' ? 'green' : 'blue'}>
                  {testResult.source || '-'}
                </Label>
                {'  '}
                缓存命中：
                <Label color={testResult.cache_hit ? 'green' : 'grey'}>
                  {testResult.cache_hit ? '是' : '否'}
                </Label>
              </p>
              <p>
                本地企业库：测试前 {testResult.local_db_before} 条 → 测试后{' '}
                {testResult.local_db_after} 条（新增 {testResult.ingested}）
              </p>
              <Table compact size='small'>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>企业名称</Table.HeaderCell>
                    <Table.HeaderCell>信用代码</Table.HeaderCell>
                    <Table.HeaderCell>法人</Table.HeaderCell>
                    <Table.HeaderCell>状态</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {sampleRows().length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan='4'>没有返回企业数据</Table.Cell>
                    </Table.Row>
                  ) : (
                    sampleRows().map((row, i) => (
                      <Table.Row key={i}>
                        <Table.Cell>{row.ENTNAME || '-'}</Table.Cell>
                        <Table.Cell>{row.UNISCID || '-'}</Table.Cell>
                        <Table.Cell>{row.FRNAME || '-'}</Table.Cell>
                        <Table.Cell>{row.ENTSTATUS || '-'}</Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table>
              <pre
                style={{
                  maxHeight: 240,
                  overflow: 'auto',
                  background: '#f6f6f6',
                  padding: 8,
                }}
              >
                {JSON.stringify(testResult.response, null, 2)}
              </pre>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setTestOpen(false)}>关闭</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ApiProductsTable;
